import { useMemo } from 'react';
import { useResolvedRequirements } from './useResolvedRequirements';
import type { MissingRequirement } from '../types/requirements';

/**
 * "What will the customer be stopped for?" — asked of the server, answered against
 * the records this screen already has.
 *
 * The LIST is the server's (`GET /api/admin/completeness/resolved`, labels
 * included). Only the presence CHECK happens here, and it deliberately mirrors the
 * server's own `isPresent`: `null`, `undefined` and whitespace-only strings are
 * blank; `0` is present, because an odometer reading of zero is a value somebody
 * gave.
 *
 * Two Send surfaces need this comparison (the estimate screen's `SendDialog` and
 * the owner dashboard's `SendEstimateModal`). It lives in one place so the two
 * cannot drift — the drift being the exact defect shape this feature exists to
 * remove.
 *
 * Fails OPEN in every direction: no records, a failed policy call, or a field
 * neither record carries a column for all produce silence rather than a false
 * accusation. The server gate is the real control; this is a heads-up.
 */

type AnyRow = Record<string, unknown>;

export interface MissingRequirementsView {
  loading: boolean;
  missing: MissingRequirement[];
}

/** Mirrors the server's `isPresent`. `0` is PRESENT. */
function isBlank(value: unknown): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim() === '';
  return false;
}

function asRow(source: unknown): AnyRow | null {
  return source && typeof source === 'object' ? (source as AnyRow) : null;
}

/** Fallback for a field the policy endpoint sent no label for. Never the bare column. */
function humanizeField(field: string): string {
  const words = field.replace(/_/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

export function useMissingRequirements(opts: {
  /** Defaults to `approval` — the stage the customer will be gated at. */
  stage?: string;
  customerId?: number | null;
  estimateId?: number | null;
  customer?: unknown;
  vehicle?: unknown;
  /** Fields already waived in this session; dropped without waiting for a refetch. */
  waived?: string[];
  enabled?: boolean;
}): MissingRequirementsView {
  const {
    stage = 'approval',
    customerId = null,
    estimateId = null,
    customer,
    vehicle,
    waived,
    enabled = true,
  } = opts;

  const requirements = useResolvedRequirements(stage, { customerId, estimateId, enabled });

  const waivedKey = (waived ?? []).join(',');

  const missing = useMemo<MissingRequirement[]>(() => {
    const customerRow = asRow(customer);
    const vehicleRow = asRow(vehicle);
    if (!customerRow && !vehicleRow) return [];

    const waivedSet = new Set(waivedKey ? waivedKey.split(',') : []);
    const out: MissingRequirement[] = [];

    for (const field of requirements.blocking) {
      if (waivedSet.has(field)) continue;

      const onCustomer = !!customerRow && field in customerRow;
      const onVehicle = !!vehicleRow && field in vehicleRow;
      // Neither record carries this column, so this screen cannot judge it.
      // `preferred_contact_channel` is the live example: a pseudo-field satisfied
      // by having a phone or email matching the stated preference, with no column
      // of its own. Reporting it would be a prompt nobody could clear.
      if (!onCustomer && !onVehicle) continue;

      const present =
        (onCustomer && !isBlank(customerRow![field])) ||
        (onVehicle && !isBlank(vehicleRow![field]));
      if (present) continue;

      out.push({
        field,
        label: requirements.labels[field] ?? humanizeField(field),
        entity: onVehicle ? 'vehicle' : 'customer',
      });
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requirements.blocking, requirements.labels, customer, vehicle, waivedKey]);

  return { loading: requirements.loading, missing };
}

export default useMissingRequirements;
